import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabList, Tab, TabPanel } from "../../../../components/core/basic/Tabs";
import { useFetch } from "../../../../hooks/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../../utils/api/index";
import Loading from "../../../../components/core/blocks/Loading";
import HarvestingConfiguration from "../../../../modules/advertising/components/HarvestingConfiguration";
import Card from "../../../../components/core/basic/Card";
import {
  BiddingProgramList,
  BudgetProgramList,
  LineItemBiddingProgramList,
} from "../../../../views/programs/ProgramList";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { ProgramContextProvider } from "../../../../components/custom/programs/ProgramContext";
import LookbackWindowConfiguration from "../../../../modules/advertising/components/LookbackWindowConfiguration";
import { successToast, errorToast } from "../../../../utils/toasts";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import { Box, VStack } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";
import HarvestingRules from "../../../../modules/advertising/components/HavestingRules";
import { TaggingRules } from "../../../../modules/advertising/components/TaggingRules";
import { DatesProvider } from "../../../../dates/DatesContext";
import useQueryParams from "../../../../hooks/useQueryParams";
import AmazonPortfolios from "../../../../components/custom/AmazonPortfolios";
import { useSimpleCan } from "../../../../config/casl/casl";
import NiceModal from "@ebay/nice-modal-react";
import { CampaignBulksheetUploadModal } from "../../../../modules/advertising/CampaignBulksheetUploadModal";
import { Button } from "../../../../components/core/basic/Button";
import { useAppSelector } from "../../../../redux/hooks";

const OrgAdvertisingConfiguration = () => {
  const { t } = useTranslation(["advertising"]);
  const [harvestingSettings, setHarvestingSettings] = useState(null);
  const [errors, setErrors] = useState({});

  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel.currentChannel);

  const hasDSPModule = useModuleEnabled("DSP");
  const hasBulkUploadModule = useModuleEnabled("BULK_CAMPAIGN_UPLOAD");
  const hasTaggingRulesModule = useModuleEnabled("TAGGING_RULES");
  const showPortfolioTab = user.is_staff && channel === "amazon";
  const [tab, setTab] = useQueryParams("tab", "rules");
  const [rulesSubTab, setRulesSubTab] = useQueryParams("rulesSubTab", "bidding");
  const userCanUpdateAds = useSimpleCan("update", "Ad");

  const orgHarvestingSettingsQuery = useFetch(
    ["orgHarvestingSettings"],
    "/org_bid_budget_settings",
    {},
    {
      default: [],
      select: (d) => d.data,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    },
  );

  const harvestingSettingsResolved = harvestingSettings ?? {
    harvestingClicks: orgHarvestingSettingsQuery.data?.harvesting_clicks,
    harvestingConversions: orgHarvestingSettingsQuery.data?.harvesting_conversions,
    harvestingDays: orgHarvestingSettingsQuery.data?.harvesting_days,
    harvestingViewImpressions: orgHarvestingSettingsQuery.data?.harvesting_view_impressions,
  };

  const saveHarvestingSettings = useMutation({
    mutationFn: async (data) => {
      await api.put(`/org_bid_budget_settings`, data);
    },

    onSuccess: () => {
      successToast({ message: t(($) => $.advertising["Successfully Updated Harvesting Settings"]) });

      queryClient.invalidateQueries();
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.advertising["Failed to Update Harvesting Settings"]), error });
    },
  });

  const updateHarvestingSettings = () => {
    const { harvestingClicks, harvestingConversions, harvestingDays, harvestingViewImpressions } =
      harvestingSettingsResolved;

    const settingsData = {
      harvesting_clicks: isNaN(Number.parseFloat(harvestingClicks)) ? null : Number.parseFloat(harvestingClicks),
      harvesting_conversions:
        isNaN(Number.parseFloat(harvestingConversions)) ? null : Number.parseFloat(harvestingConversions),
      harvesting_days: isNaN(Number.parseFloat(harvestingDays)) ? null : Number.parseFloat(harvestingDays),
      harvesting_view_impressions:
        isNaN(Number.parseFloat(harvestingViewImpressions)) ? null : Number.parseFloat(harvestingViewImpressions),
    };

    saveHarvestingSettings.mutate(settingsData);
  };

  const handleChange = (e) => {
    if (
      Object.values({
        ...harvestingSettingsResolved,
        [e.target.name]: e.target.value,
      }).some((setting) => !setting)
    ) {
      setErrors({ harvestingRules: t(($) => $.advertising["All fields are required."]) });
    } else {
      setErrors({});
    }

    setHarvestingSettings({
      ...harvestingSettingsResolved,
      [e.target.name]: e.target.value,
    });
  };

  if (orgHarvestingSettingsQuery.isLoading) {
    return <Loading fullPage={true} />;
  }

  return (
    <PageContainer>
      <VStack alignItems={"stretch"} width="100%" paddingX="xxlarge" paddingY="large" alignSelf={"center"} gap="medium">
        <h3
          className={css({
            margin: 0,
            fontWeight: "bold",
            textAlign: "center",
          })}
        >
          {t(($) => $.advertising["Advertising - Configuration"])}
        </h3>
        {hasBulkUploadModule && (
          <Button
            onPress={() => {
              void NiceModal.show(CampaignBulksheetUploadModal);
            }}
            className={css({ width: "fit-content" })}
          >
            {t(($) => $.advertising["Campaign Bulksheet Upload"])}
          </Button>
        )}
        <Card>
          <Tabs selectedKey={tab} onSelectionChange={setTab}>
            <VStack alignItems="stretch" width="100%">
              <TabList>
                <Tab id="rules">{t(($) => $.advertising["Programs"])}</Tab>
                <Tab id="algorithms">{t(($) => $.advertising["Bidding Algorithms"])}</Tab>
                {showPortfolioTab && <Tab id="portfolios">{t(($) => $.advertising["Portfolios"])}</Tab>}
              </TabList>

              <TabPanel id="rules">
                <Tabs orientation="vertical" selectedKey={rulesSubTab} onSelectionChange={setRulesSubTab}>
                  <TabList>
                    <Tab id="bidding">{t(($) => $.advertising["Bidding"])}</Tab>
                    <Tab id="harvesting">{t(($) => $.advertising["Harvesting"])}</Tab>
                    <Tab id="custom_harvesting">{t(($) => $.advertising["Custom Harvesting"])}</Tab>
                    <Tab id="campaigns">{t(($) => $.advertising["Campaigns"])}</Tab>
                    {hasTaggingRulesModule && <Tab id="tagging">{t(($) => $.advertising["Tagging"])}</Tab>}
                    {hasDSPModule && <Tab id="lineitem_bidding">{t(($) => $.advertising["Line Items"])}</Tab>}
                  </TabList>

                  <TabPanel id="bidding">
                    <ProgramContextProvider engine={"bidding"}>
                      <h3 className={programsHeadingStyles}>{t(($) => $.advertising["Bidding Programs"])}</h3>
                      <BiddingProgramList />
                    </ProgramContextProvider>
                  </TabPanel>

                  <TabPanel id="harvesting">
                    <HarvestingConfiguration
                      handleChange={handleChange}
                      state={harvestingSettingsResolved}
                      onChange={(partialSettings) => {
                        if (
                          Object.values({
                            ...harvestingSettingsResolved,
                            ...partialSettings,
                          }).some((setting) => !setting && isNaN(parseInt(setting)))
                        ) {
                          setErrors({
                            harvestingRules: t(($) => $.advertising["All fields are required."]),
                          });
                        } else {
                          setErrors({});
                        }
                        setHarvestingSettings({
                          ...harvestingSettingsResolved,
                          ...partialSettings,
                        });
                      }}
                      saveHarvestingSettings={updateHarvestingSettings}
                      disableSubmitButton={saveHarvestingSettings.isPending || Object.keys(errors)?.length}
                      orgLayout={true}
                      readOnly={!userCanUpdateAds}
                    />
                  </TabPanel>

                  <TabPanel id="custom_harvesting">
                    <DatesProvider>
                      <HarvestingRules />
                    </DatesProvider>
                  </TabPanel>

                  <TabPanel id="campaigns">
                    <ProgramContextProvider engine={"budget"}>
                      <h3 className={programsHeadingStyles}>{t(($) => $.advertising["Campaign Programs"])}</h3>
                      <BudgetProgramList />
                    </ProgramContextProvider>
                  </TabPanel>

                  {hasTaggingRulesModule && (
                    <TabPanel id="tagging">
                      <TaggingRules />
                    </TabPanel>
                  )}

                  {hasDSPModule && (
                    <TabPanel id="lineitem_bidding">
                      <ProgramContextProvider engine={"lineitem_bidding"}>
                        <LineItemBiddingProgramList />
                      </ProgramContextProvider>
                    </TabPanel>
                  )}
                </Tabs>
              </TabPanel>

              <TabPanel id="algorithms">
                <LookbackWindowConfiguration />
              </TabPanel>

              {showPortfolioTab && (
                <TabPanel id="portfolios">
                  <Box flex={1}>
                    <AmazonPortfolios />
                  </Box>
                </TabPanel>
              )}
            </VStack>
          </Tabs>
        </Card>
      </VStack>
    </PageContainer>
  );
};

// Matches the Tagging tab heading.
const programsHeadingStyles = css({ fontWeight: "bold", paddingTop: "xsmall", paddingBottom: "medium" });
export const Route = createFileRoute("/_authenticated/user/advertising/settings")({
  component: OrgAdvertisingConfiguration,
  staticData: { application: "Advertising" },
});
